import { taskRecordComplete } from './building-onboarding.js';
export const operationalReadinessLabels = {
    essential: 'Essential action required', outstanding: 'Work outstanding', current: 'Up to date',
    unknown: 'Not assessed', 'not-applicable': 'Not applicable',
};
export const buildingSetupAreas = [
    { id: 'authority', label: 'Client, agreement and authority', groups: ['authority', 'commercial'] },
    { id: 'building', label: 'Building, properties and people', groups: ['profile', 'properties'] },
    { id: 'finance', label: 'Service charges and finance', groups: ['charging', 'finance'] },
    { id: 'services', label: 'Assets, suppliers and services', groups: ['assets', 'suppliers'] },
    { id: 'safety', label: 'Condition, safety and insurance', groups: ['condition', 'safety', 'insurance'] },
    { id: 'handover', label: 'Access, communications and handover', groups: ['launch', 'acceptance'] },
];
/** One definition shared by counts, the guided route and navigation. N/A is settled,
 * never a fabricated Complete fact. Outstanding Work does not certify source records. */
export function taskNeedsSetup(task) {
    return task.applicability !== 'Not applicable' && !taskRecordComplete(task);
}
export function operationalReadiness(tasks) {
    const applicable = tasks.filter(task => task.applicability !== 'Not applicable');
    const essential = applicable.filter(task => task.accessible && task.criticalIssues.length);
    const outstanding = applicable.filter(taskNeedsSetup);
    // Access failures and unknown applicability cannot produce a reassuring green.
    const unassessed = applicable.filter(task => !task.accessible || task.applicability === 'Unknown');
    const state = !tasks.length ? 'unknown' : essential.length ? 'essential'
        : unassessed.length ? (outstanding.some(task => task.accessible && task.state !== 'Unknown') ? 'outstanding' : 'unknown')
            : !applicable.length ? 'not-applicable' : !outstanding.length ? 'current'
                : applicable.every(task => task.state === 'Unknown' && !task.note) ? 'unknown' : 'outstanding';
    return { state, label: operationalReadinessLabels[state], completed: applicable.filter(taskRecordComplete).length,
        total: applicable.length, essential: essential.length, outstanding: outstanding.length, unassessed: unassessed.length };
}
export function guidedBuildingSetup(model) {
    const tasks = model.groups.flatMap(group => group.tasks);
    const areas = buildingSetupAreas.map(area => {
        const areaTasks = tasks.filter(task => area.groups.includes(task.group));
        const ongoing = (model.operationalAttention ?? []).filter(item => area.groups.includes(item.group) && item.count > 0);
        const readiness = operationalReadiness(areaTasks);
        const state = readiness.state === 'essential' || ongoing.some(item => item.essential) ? 'essential'
            : ongoing.length ? 'outstanding' : readiness.state;
        return { ...area, tasks: areaTasks, ongoing, readiness: { ...readiness, state, label: operationalReadinessLabels[state] } };
    });
    // Stable domain order within a priority: dependent building facts are collected
    // before later handover review. Critical issues remain visible at every stage.
    const next = tasks.filter(task => task.accessible && taskNeedsSetup(task))
        .sort((a, b) => Number(Boolean(b.criticalIssues.length)) - Number(Boolean(a.criticalIssues.length)))[0] ?? null;
    const readiness = operationalReadiness(tasks), ongoing = model.operationalAttention?.filter(item => item.count > 0) ?? [];
    const state = readiness.state === 'essential' || ongoing.some(item => item.essential) ? 'essential' : ongoing.length ? 'outstanding' : readiness.state;
    return { areas, tasks, readiness: { ...readiness, state, label: operationalReadinessLabels[state] }, next };
}
/** Only explicit source coverage colours an operational area. We do not infer
 * readiness of every filtered queue from a neighbouring register, or read a
 * different building through a return URL. Unknown areas remain neutral. */
export function readinessForBuildingLink(model, href) {
    const url = new URL(href, 'https://staff.invalid');
    const pathBuilding = /^\/buildings\/([^/]+)/.exec(url.pathname)?.[1];
    if ((pathBuilding && pathBuilding !== model.building.id)
        || (url.searchParams.has('building') && url.searchParams.get('building') !== model.building.id))
        return undefined;
    const tasks = model.groups.flatMap(group => group.tasks);
    if (url.pathname === `/buildings/${model.building.id}/genie`)
        return guidedBuildingSetup(model).readiness;
    const filtered = [...url.searchParams.keys()].some(key => key !== 'building');
    const ongoing = model.operationalAttention?.filter(item => !filtered && item.count > 0 && (new URL(item.href, 'https://staff.invalid').pathname === url.pathname
        || ['/work/all', '/engines/repairs', '/work/operating'].includes(url.pathname) && item.group === 'condition'
        || url.pathname === '/engines/operations_compliance' && item.group === 'safety')) ?? [];
    if (ongoing.length) {
        const readiness = operationalReadiness(tasks.filter(task => ongoing.some(item => item.group === task.group)));
        const state = readiness.state === 'essential' || ongoing.some(item => item.essential) ? 'essential' : 'outstanding';
        return { ...readiness, state, label: operationalReadinessLabels[state] };
    }
    // A received-document/contacts handover check does not certify an entire live inbox or directory.
    if (['/documents', '/contacts', '/communications', '/calendar'].includes(url.pathname))
        return undefined;
    const buildingSection = /^\/buildings\/[^/]+\/([^/]+)$/.exec(url.pathname)?.[1];
    const recordTasks = {
        information: ['details', 'profile', 'keys'],
        'management-fees': ['agreement', 'fees'],
        'legal-particulars': ['ownership'],
    };
    if (buildingSection && !filtered && recordTasks[buildingSection]) {
        return operationalReadiness(tasks.filter(task => recordTasks[buildingSection].includes(task.id)));
    }
    const sameRecord = tasks.filter(task => {
        const target = new URL(task.href, 'https://staff.invalid');
        const selection = (value) => {
            const parameters = new URLSearchParams([...value.searchParams].filter(([key]) => !['building', 'returnTo', 'mode'].includes(key)));
            parameters.sort();
            return parameters.toString();
        };
        return target.pathname === url.pathname && selection(target) === selection(url);
    });
    if (sameRecord.length)
        return operationalReadiness(sameRecord);
    const groupRoutes = {
        '/engines/finance': ['finance', 'charging'], '/engines/assets': ['assets'], '/engines/property_admin': ['properties'],
        '/engines/operations_compliance': ['condition', 'safety'], '/engines/insurance': ['insurance'],
        '/engines/suppliers': ['suppliers'], '/engines/onboarding': [],
        '/assets': ['assets'], '/insurance': ['insurance'], '/compliance': ['safety'],
    };
    const groups = filtered ? undefined : groupRoutes[url.pathname];
    return groups ? operationalReadiness(tasks.filter(task => groups.includes(task.group))) : undefined;
}
//# sourceMappingURL=building-readiness.js.map